<template>
  <external-controller title="Acompanhamento do projeto" :back="false">
    <main class="public-gantt-page" aria-labelledby="public-gantt-title">
      <header class="public-gantt-header">
        <div>
          <span class="public-gantt-eyebrow">Nivvo</span>
          <h1 id="public-gantt-title">Gantt do projeto</h1>
          <p>Acompanhe o planejamento em modo somente leitura.</p>
        </div>
        <span class="read-only-badge">
          <fa :icon="['far', 'eye']" aria-hidden="true"></fa>
          Somente leitura
        </span>
      </header>

      <section v-if="error" class="public-gantt-state public-gantt-error" role="alert">
        <fa :icon="['fas', 'triangle-exclamation']" aria-hidden="true"></fa>
        <div>
          <h2>Não foi possível abrir o Gantt</h2>
          <p>{{ error }}</p>
        </div>
      </section>

      <section v-else class="public-gantt-frame" :class="{ loaded }">
        <div v-if="!loaded" class="public-gantt-state" role="status" aria-live="polite">
          <span class="loader" aria-hidden="true"></span>
          <p>Carregando planejamento...</p>
        </div>
        <iframe
          :src="ganttUrl"
          title="Gantt público do projeto no Nivvo"
          sandbox=""
          referrerpolicy="no-referrer"
          @load="loaded = true"
        ></iframe>
      </section>
    </main>
  </external-controller>
</template>

<script>
export default {
  name: 'NivvoPublicGantt',
  props: {
    projectId: {
      type: String,
      required: true
    },
    expires: {
      type: [String, Array],
      default: null
    },
    signature: {
      type: [String, Array],
      default: null
    }
  },
  data () {
    return {
      error: null,
      ganttUrl: null,
      loaded: false
    }
  },
  created () {
    this.prepareGanttUrl()
  },
  methods: {
    prepareGanttUrl () {
      const expires = Array.isArray(this.expires) ? this.expires[0] : this.expires
      const signature = Array.isArray(this.signature) ? this.signature[0] : this.signature

      if (!/^\d+$/.test(this.projectId) || !/^\d+$/.test(expires || '') || !/^[a-f0-9]{64}$/i.test(signature || '')) {
        this.error = 'O link está incompleto ou foi alterado. Solicite um novo link ao espaço responsável.'
        return
      }

      const nivvoOrigin = this.resolveNivvoOrigin()
      if (!nivvoOrigin) {
        this.error = 'A visualização pública do Nivvo não está configurada neste ambiente.'
        return
      }

      const url = new URL(`/public/projects/${this.projectId}/gantt`, `${nivvoOrigin}/`)
      url.searchParams.set('expires', expires)
      url.searchParams.set('signature', signature)
      this.ganttUrl = url.toString()
    },
    resolveNivvoOrigin () {
      const configuredUrl = process.env.VUE_APP_NIVVO_URL
      if (configuredUrl) return this.validOrigin(configuredUrl)

      if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
        return 'http://localhost:8088'
      }

      if (!window.location.hostname.startsWith('apollo.')) return null
      return `${window.location.protocol}//${window.location.hostname.replace(/^apollo\./, 'nivvo.')}`
    },
    validOrigin (value) {
      try {
        const url = new URL(value)
        if (url.protocol !== 'https:' && url.hostname !== 'localhost') return null
        return url.origin
      } catch {
        return null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.public-gantt-page {
  margin: 0 auto;
  max-width: 1600px;
  padding: $hmg;
  width: 100%;
}

.public-gantt-header {
  align-items: flex-start;
  display: flex;
  gap: $hmg;
  justify-content: space-between;
  margin: $hmg 0;

  h1 { color: $color-text; font-size: 24px; line-height: 32px; margin: 4px 0; }
  p { color: $color-text; margin: 0; }
}

.public-gantt-eyebrow {
  color: $color-primary;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.read-only-badge {
  align-items: center;
  background: $color-secondary;
  border: $border-component;
  border-radius: 9999px;
  color: $color-text;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  gap: $mg_mini;
  min-height: 40px;
  padding: 0 $hmg_small;
}

.public-gantt-frame {
  background: $color-secondary;
  border: $border-component;
  border-radius: $border-radius;
  min-height: 520px;
  overflow: hidden;
  position: relative;

  iframe {
    border: 0;
    display: block;
    height: calc(100vh - 220px);
    min-height: 520px;
    opacity: 0;
    width: 100%;
  }

  &.loaded iframe { opacity: 1; }
}

.public-gantt-state {
  align-items: center;
  color: $color-text;
  display: flex;
  gap: $hmg_small;
  justify-content: center;
  min-height: 520px;
  padding: $hmg;
  text-align: center;

  h2 { color: $color-text; font-size: 20px; margin: 0 0 $mg_mini; }
  p { margin: 0; }
}

.public-gantt-frame .public-gantt-state { inset: 0; position: absolute; }

.public-gantt-error {
  background: $color-secondary;
  border: $border-component;
  border-radius: $border-radius;

  > svg { color: $color-error; font-size: 24px; }
}

@media (max-width: 767px) {
  .public-gantt-page { padding: $hmg_small; }
  .public-gantt-header { align-items: stretch; flex-direction: column; }
  .read-only-badge { align-self: flex-start; }
  .public-gantt-frame,
  .public-gantt-state,
  .public-gantt-frame iframe { min-height: calc(100vh - 260px); }
  .public-gantt-frame iframe { height: calc(100vh - 260px); }
}
</style>
